import { useState, useEffect, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '../../components/ui/table';
import { Loader2, Trophy, Medal } from 'lucide-react';
import { TraineeFilterBar, useTraineeFilter } from './TraineeFilterContext';

const METRICS = [
  { value: 'composite', label: 'Composite (50% attendance + 50% assessment)' },
  { value: 'attendance', label: 'Attendance only' },
  { value: 'assessment', label: 'Assessment only' },
];
const PODIUM = ['text-amber-500', 'text-slate-400', 'text-orange-600'];

export default function LeaderboardTab() {
  const { get } = useApi();
  const { scopeQS } = useTraineeFilter();
  const [metric, setMetric] = useState('composite');
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const scope = scopeQS();
      const res = await get(`/trainees/leaderboard?metric=${metric}${scope ? `&${scope}` : ''}`);
      setRows(res.leaderboard || []);
      setMeta(res);
    } catch {
      setRows([]);
      setMeta({});
    } finally { setLoading(false); }
  }, [get, scopeQS, metric]);

  useEffect(() => { load(); }, [load]);

  const top3 = rows.filter(r => r.score != null).slice(0, 3);
  const dash = (v, suffix = '%') => (v == null
    ? <span className="text-muted-foreground" title="No assessment assigned or attempted in this batch" data-testid="lb-dash">—</span>
    : `${v}${suffix}`);

  return (
    <div className="space-y-6" data-testid="leaderboard-tab">
      <TraineeFilterBar extra={
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground">Rank by</label>
          <Select value={metric} onValueChange={setMetric}>
            <SelectTrigger className="w-80" data-testid="lb-metric-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {METRICS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      } />

      <p className="text-xs text-muted-foreground" data-testid="lb-legend">
        {meta.mode === 'batch'
          ? <>Ranking <strong>{meta.batch_name}</strong> on its own data · 50% attendance + 50% assessment · assessments assigned to this batch ({meta.assessment_count ?? 0}) · unattempted = 0 · “—” = no assessment attempted in this batch (ranked last)</>
          : <>Ranking each agent on their <strong>current batch</strong> · 50% attendance + 50% assessment · pick a single batch for batch-accurate scores</>}
      </p>

      {top3.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {top3.map((r, i) => (
            <Card key={r.trainee_id} className="p-5 flex items-center gap-4" data-testid={`lb-podium-${i + 1}`}>
              <Trophy className={`w-8 h-8 ${PODIUM[i]}`} />
              <div className="min-w-0">
                <div className="text-xs text-muted-foreground">Rank #{r.rank}</div>
                <div className="font-display font-bold truncate">{r.name}</div>
                <div className="text-2xl font-display font-bold tabular-nums">{r.score}</div>
                <div className="text-xs text-muted-foreground">{r.batch_name || '—'}</div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">Rank</TableHead>
                <TableHead>Trainee</TableHead>
                <TableHead>Batch</TableHead>
                <TableHead className="text-right">Attendance</TableHead>
                <TableHead className="text-right">Assessment</TableHead>
                <TableHead className="text-right">Score</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">No trainees to rank.</TableCell></TableRow>}
              {rows.map(r => (
                <TableRow key={r.trainee_id} data-testid={`lb-row-${r.trainee_id}`}>
                  <TableCell>
                    {r.rank <= 3 && r.score != null
                      ? <Medal className={`w-4 h-4 ${PODIUM[r.rank - 1]}`} />
                      : <span className="text-muted-foreground tabular-nums">{r.rank}</span>}
                  </TableCell>
                  <TableCell className="font-medium">{r.name}{r.employee_id ? <span className="text-muted-foreground text-xs ml-2">{r.employee_id}</span> : null}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{r.batch_name || '—'}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.attendance_pct}%</TableCell>
                  <TableCell className="text-right tabular-nums">{dash(r.assessment_pct)}</TableCell>
                  <TableCell className="text-right font-semibold tabular-nums">{dash(r.score, '')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
